import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Mic, X, Send, Loader2, Sparkles, MicOff, Check, Calendar, FolderOpen,
  MessageCircle, Image, Video, FileText, Zap, ChevronRight, Globe, ArrowRight, Languages,
  Volume2, VolumeX
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Supported languages - same as landing page
const LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "ur", name: "اردو", flag: "🇵🇰" },
  { code: "zh", name: "中文", flag: "🇨🇳" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "pt", name: "Português", flag: "🇧🇷" },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  { code: "hi", name: "हिंदी", flag: "🇮🇳" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
];

// Language code to speechSynthesis voice language mapping
const LANG_TO_VOICE = {
  en: 'en-US',
  ar: 'ar-SA',
  es: 'es-ES',
  ur: 'ur-PK',
  zh: 'zh-CN',
  fr: 'fr-FR',
  de: 'de-DE',
  pt: 'pt-BR',
  ja: 'ja-JP',
  ko: 'ko-KR',
  hi: 'hi-IN',
  ru: 'ru-RU',
};

export default function VoiceCommandModal({ isOpen, onClose, onSuccess }) {
  const [message, setMessage] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [showLanguageSelect, setShowLanguageSelect] = useState(true); // NEW: Start with language selection
  const [showWebsiteInput, setShowWebsiteInput] = useState(false); // NEW: Website input step
  const [showConversation, setShowConversation] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Get saved language from localStorage or default to 'en'
    return localStorage.getItem('yasno_language') || 'en';
  });
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [campaignPlan, setCampaignPlan] = useState(null);
  const [phase, setPhase] = useState("gathering"); // gathering, planning, confirmation, executing, done
  const [isExecuting, setIsExecuting] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    // Get saved voice preference from localStorage or default to true
    const saved = localStorage.getItem('yasno_voice_enabled');
    return saved === null ? true : saved === 'true';
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const speechSynthRef = useRef(null);
  const voiceEnabledRef = useRef(voiceEnabled);
  
  // Keep ref in sync with state
  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
  }, [voiceEnabled]);
  
  // Load voices on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      // Load voices
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          setVoicesLoaded(true);
          console.log('Voices loaded:', voices.length);
        }
      };
      
      // Try to load immediately
      loadVoices();
      
      // Also listen for voiceschanged event (needed for Chrome)
      window.speechSynthesis.onvoiceschanged = loadVoices;
      
      return () => {
        window.speechSynthesis.onvoiceschanged = null;
      };
    }
  }, []);

  // Speech synthesis function - YASNO speaks back!
  const speakText = (text) => {
    // Use ref to get current value (avoids stale closure)
    if (!voiceEnabledRef.current) {
      console.log('Voice disabled, skipping speech');
      return;
    }
    if (!window.speechSynthesis) {
      console.log('Speech synthesis not supported');
      return;
    }
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    // Clean text for natural speech - remove ALL markdown and special formatting
    let cleanText = text
      // Remove emojis
      .replace(/[\u{1F600}-\u{1F6FF}]/gu, '')
      .replace(/[\u{2600}-\u{26FF}]/gu, '')
      .replace(/[\u{2700}-\u{27BF}]/gu, '')
      .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
      .replace(/[\u{1F900}-\u{1F9FF}]/gu, '')
      .replace(/[🚀👋✨💡🎯📊🔥🌐⏳✅⚠️📋🎬📱💼🏢📈📉🔄💰🎨🖼️📝🔗]/g, '')
      // Remove markdown headers (# ## ### etc)
      .replace(/^#{1,6}\s*/gm, '')
      // Remove markdown bold/italic (**text** or *text* or __text__ or _text_)
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      // Remove markdown bullet points (- or * at start of line)
      .replace(/^[\-\*]\s+/gm, '')
      // Remove numbered lists (1. 2. etc) but keep the text
      .replace(/^\d+\.\s+/gm, '')
      // Remove markdown links [text](url) - keep just the text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove standalone URLs
      .replace(/https?:\/\/[^\s]+/g, '')
      // Remove bullet point symbols
      .replace(/•/g, '')
      // Remove extra asterisks or underscores
      .replace(/[\*_]{1,2}/g, '')
      // Remove (s) patterns like "platform(s)"
      .replace(/\(s\)/gi, 's')
      // Clean up multiple spaces
      .replace(/\s+/g, ' ')
      // Replace newlines with pauses
      .replace(/\n+/g, '. ')
      // Remove multiple periods
      .replace(/\.{2,}/g, '.')
      // Clean up spaces before punctuation
      .replace(/\s+([.,!?])/g, '$1')
      .trim();
    
    if (!cleanText) {
      console.log('No text to speak after cleaning');
      return;
    }
    
    console.log('Speaking:', cleanText.substring(0, 100) + '...');
    
    // Simple implementation that works
    const utterance = new SpeechSynthesisUtterance(cleanText);
    
    // Set language
    const voiceLang = LANG_TO_VOICE[selectedLanguage] || 'en-US';
    utterance.lang = voiceLang;
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    // Try to get a voice for the language
    const voices = window.speechSynthesis.getVoices();
    const matchingVoice = voices.find(v => v.lang.startsWith(voiceLang.split('-')[0]));
    if (matchingVoice) {
      utterance.voice = matchingVoice;
    }
    
    utterance.onstart = () => {
      console.log('Speech started');
      setIsSpeaking(true);
    };
    
    utterance.onend = () => {
      console.log('Speech ended');
      setIsSpeaking(false);
    };
    
    utterance.onerror = (e) => {
      console.log('Speech error:', e.error);
      setIsSpeaking(false);
    };
    
    speechSynthRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  // Stop speaking
  const stopSpeaking = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  // Toggle voice on/off
  const toggleVoice = () => {
    const newValue = !voiceEnabled;
    setVoiceEnabled(newValue);
    localStorage.setItem('yasno_voice_enabled', String(newValue));
    if (!newValue) {
      stopSpeaking();
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSessionId(crypto.randomUUID());
      setConversationHistory([]);
      setCampaignPlan(null);
      setPhase("gathering");
      setIsExecuting(false);
      setShowLanguageSelect(true); // Start with language selection
      setShowWebsiteInput(false);
      setShowConversation(false);
      setWebsiteUrl("");
      setMessage("");
      // Stop any ongoing speech
      stopSpeaking();
      // Load saved language
      const savedLang = localStorage.getItem('yasno_language');
      if (savedLang) {
        setSelectedLanguage(savedLang);
      }
    } else {
      // Modal closing - stop speech
      stopSpeaking();
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationHistory]);

  const getUserId = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub;
      } catch (e) { return null; }
    }
    return null;
  };

  // Handle language selection and proceed to website input
  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode);
    localStorage.setItem('yasno_language', langCode);
    setShowLanguageSelect(false);
    setShowWebsiteInput(true);
  };

  // Start conversation with optional website URL
  const startConversation = async () => {
    setShowWebsiteInput(false);
    setShowConversation(true);
    
    // Set initial greeting based on selected language
    const greetings = {
      en: "👋 Hi! I'm YASNO, your AI marketing strategist. Tell me about your business and what you'd like to promote. I'll create a personalized campaign plan with the right content types for each platform before we start creating anything.",
      ar: "👋 مرحباً! أنا ياسنو، استراتيجي التسويق الذكي الخاص بك. أخبرني عن عملك وما تريد الترويج له. سأقوم بإنشاء خطة حملة مخصصة مع أنواع المحتوى المناسبة لكل منصة قبل أن نبدأ في إنشاء أي شيء.",
      es: "👋 ¡Hola! Soy YASNO, tu estratega de marketing con IA. Cuéntame sobre tu negocio y qué te gustaría promocionar. Crearé un plan de campaña personalizado con los tipos de contenido adecuados para cada plataforma antes de comenzar a crear algo.",
      fr: "👋 Bonjour! Je suis YASNO, votre stratège marketing IA. Parlez-moi de votre entreprise et de ce que vous souhaitez promouvoir. Je créerai un plan de campagne personnalisé avec les bons types de contenu pour chaque plateforme avant de commencer à créer quoi que ce soit.",
      de: "👋 Hallo! Ich bin YASNO, Ihr KI-Marketing-Stratege. Erzählen Sie mir von Ihrem Unternehmen und was Sie bewerben möchten. Ich erstelle einen personalisierten Kampagnenplan mit den richtigen Inhaltstypen für jede Plattform, bevor wir mit der Erstellung beginnen.",
      zh: "👋 你好！我是YASNO，你的AI营销策略师。告诉我你的业务以及你想推广什么。在我们开始创建任何内容之前，我会为每个平台制定个性化的活动计划。",
      ja: "👋 こんにちは！私はYASNO、あなたのAIマーケティングストラテジストです。あなたのビジネスと宣伝したいことを教えてください。コンテンツを作成する前に、各プラットフォームに適したコンテンツタイプでパーソナライズされたキャンペーンプランを作成します。",
      ko: "👋 안녕하세요! 저는 YASNO, 당신의 AI 마케팅 전략가입니다. 비즈니스와 홍보하고 싶은 내용을 알려주세요. 콘텐츠를 만들기 전에 각 플랫폼에 맞는 맞춤형 캠페인 계획을 만들어 드리겠습니다.",
      pt: "👋 Olá! Sou YASNO, seu estrategista de marketing com IA. Conte-me sobre seu negócio e o que você gostaria de promover. Criarei um plano de campanha personalizado com os tipos de conteúdo certos para cada plataforma antes de começarmos a criar qualquer coisa.",
      hi: "👋 नमस्ते! मैं YASNO हूं, आपका AI मार्केटिंग रणनीतिकार। मुझे अपने व्यवसाय और जो आप प्रचारित करना चाहते हैं उसके बारे में बताएं। कुछ भी बनाना शुरू करने से पहले मैं प्रत्येक प्लेटफॉर्म के लिए सही सामग्री प्रकारों के साथ एक व्यक्तिगत अभियान योजना बनाऊंगा।",
      ru: "👋 Привет! Я YASNO, ваш AI-маркетолог. Расскажите мне о вашем бизнесе и о том, что вы хотите продвигать. Я создам персонализированный план кампании с правильными типами контента для каждой платформы, прежде чем мы начнем что-либо создавать.",
      ur: "👋 ہیلو! میں YASNO ہوں، آپ کا AI مارکیٹنگ اسٹریٹجسٹ۔ مجھے اپنے کاروبار کے بارے میں بتائیں اور آپ کیا پروموٹ کرنا چاہتے ہیں۔ میں کچھ بھی بنانا شروع کرنے سے پہلے ہر پلیٹ فارم کے لیے صحیح مواد کی اقسام کے ساتھ ایک ذاتی مہم کا منصوبہ بناؤں گا۔"
    };
    
    const greeting = greetings[selectedLanguage] || greetings.en;
    
    if (websiteUrl.trim()) {
      // If user provided a website, analyze it first
      setLoading(true);
      
      // Localized "Analyzing" message
      const analyzingMessages = {
        en: `🌐 Analyzing your website: ${websiteUrl}...\n\nPlease wait while I learn about your business.`,
        ar: `🌐 جاري تحليل موقعك: ${websiteUrl}...\n\nيرجى الانتظار بينما أتعرف على عملك.`,
        es: `🌐 Analizando tu sitio web: ${websiteUrl}...\n\nPor favor espera mientras aprendo sobre tu negocio.`,
        fr: `🌐 Analyse de votre site web: ${websiteUrl}...\n\nVeuillez patienter pendant que j'apprends à connaître votre entreprise.`,
        de: `🌐 Analysiere Ihre Website: ${websiteUrl}...\n\nBitte warten Sie, während ich Ihr Unternehmen kennenlerne.`,
        zh: `🌐 正在分析您的网站: ${websiteUrl}...\n\n请稍候，我正在了解您的业务。`,
        ja: `🌐 ウェブサイトを分析中: ${websiteUrl}...\n\nお待ちください。ビジネスについて学んでいます。`,
        ko: `🌐 웹사이트 분석 중: ${websiteUrl}...\n\n비즈니스에 대해 알아보는 동안 잠시 기다려 주세요.`,
        pt: `🌐 Analisando seu site: ${websiteUrl}...\n\nAguarde enquanto aprendo sobre seu negócio.`,
        hi: `🌐 आपकी वेबसाइट का विश्लेषण: ${websiteUrl}...\n\nकृपया प्रतीक्षा करें जब तक मैं आपके व्यवसाय के बारे में जानता हूं।`,
        ru: `🌐 Анализирую ваш сайт: ${websiteUrl}...\n\nПодождите, пока я изучаю ваш бизнес.`,
        ur: `🌐 آپ کی ویب سائٹ کا تجزیہ: ${websiteUrl}...\n\nبراہ کرم انتظار کریں جب تک میں آپ کے کاروبار کے بارے میں جانتا ہوں۔`
      };
      
      const analyzingText = analyzingMessages[selectedLanguage] || analyzingMessages.en;
      setConversationHistory([{
        role: "assistant",
        content: analyzingText
      }]);
      // Speak the analyzing message
      speakText(analyzingText.replace(/🌐/g, '').replace(/\n+/g, ' ').trim());
      
      try {
        const token = localStorage.getItem('token');
        const userId = getUserId();
        
        const response = await fetch(`${API}/planning/message`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({ 
            session_id: sessionId,
            message: `Check my website ${websiteUrl}`,
            user_id: userId,
            conversation_history: [],
            language: selectedLanguage
          })
        });
        
        if (!response.ok) throw new Error('Request failed');
        
        const result = await response.json();
        
        setConversationHistory([
          { role: "user", content: `Check my website ${websiteUrl}` },
          { role: "assistant", content: result.response }
        ]);
        
        // Speak the response aloud
        speakText(result.response);
        
        setPhase(result.phase || "gathering");
        
        if (result.campaign_plan) {
          setCampaignPlan(result.campaign_plan);
        }
        
      } catch (error) {
        console.error("Website analysis error:", error);
        setConversationHistory([{
          role: "assistant",
          content: greeting
        }]);
        // Speak the greeting
        speakText(greeting);
        toast.error("Couldn't analyze website, but let's continue!");
      } finally {
        setLoading(false);
      }
    } else {
      // No website provided, start with standard greeting
      setConversationHistory([{
        role: "assistant",
        content: greeting
      }]);
      // Speak the greeting
      speakText(greeting);
    }
  };

  // Skip website and go straight to conversation
  const skipWebsite = () => {
    setShowWebsiteInput(false);
    setShowConversation(true);
    
    // Get greeting in selected language
    const greetings = {
      en: "👋 Hi! I'm YASNO, your AI marketing strategist. Tell me about your business and what you'd like to promote. I'll create a personalized campaign plan with the right content types for each platform before we start creating anything.",
      ar: "👋 مرحباً! أنا ياسنو، استراتيجي التسويق الذكي الخاص بك. أخبرني عن عملك وما تريد الترويج له.",
      es: "👋 ¡Hola! Soy YASNO, tu estratega de marketing con IA. Cuéntame sobre tu negocio y qué te gustaría promocionar.",
      fr: "👋 Bonjour! Je suis YASNO, votre stratège marketing IA. Parlez-moi de votre entreprise et de ce que vous souhaitez promouvoir.",
      de: "👋 Hallo! Ich bin YASNO, Ihr KI-Marketing-Stratege. Erzählen Sie mir von Ihrem Unternehmen und was Sie bewerben möchten.",
      zh: "👋 你好！我是YASNO，你的AI营销策略师。告诉我你的业务以及你想推广什么。",
      ja: "👋 こんにちは！私はYASNO、あなたのAIマーケティングストラテジストです。あなたのビジネスと宣伝したいことを教えてください。",
      ko: "👋 안녕하세요! 저는 YASNO, 당신의 AI 마케팅 전략가입니다. 비즈니스와 홍보하고 싶은 내용을 알려주세요.",
      pt: "👋 Olá! Sou YASNO, seu estrategista de marketing com IA. Conte-me sobre seu negócio e o que você gostaria de promover.",
      hi: "👋 नमस्ते! मैं YASNO हूं, आपका AI मार्केटिंग रणनीतिकार। मुझे अपने व्यवसाय के बारे में बताएं।",
      ru: "👋 Привет! Я YASNO, ваш AI-маркетолог. Расскажите мне о вашем бизнесе и о том, что вы хотите продвигать.",
      ur: "👋 ہیلو! میں YASNO ہوں، آپ کا AI مارکیٹنگ اسٹریٹجسٹ۔ مجھے اپنے کاروبار کے بارے میں بتائیں۔"
    };
    
    const greetingText = greetings[selectedLanguage] || greetings.en;
    setConversationHistory([{
      role: "assistant",
      content: greetingText
    }]);
    // Speak the greeting
    speakText(greetingText);
  };

  const sendMessage = async () => {
    if (!message.trim() || loading) return;
    
    const userMessage = message.trim();
    setMessage("");
    setLoading(true);
    
    // Add user message to history
    const updatedHistory = [...conversationHistory, { role: "user", content: userMessage }];
    setConversationHistory(updatedHistory);
    
    try {
      const token = localStorage.getItem('token');
      const userId = getUserId();
      
      const response = await fetch(`${API}/planning/message`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          session_id: sessionId,
          message: userMessage,
          user_id: userId,
          conversation_history: updatedHistory.slice(1), // Skip initial greeting
          language: selectedLanguage
        })
      });
      
      if (!response.ok) throw new Error('Request failed');
      
      const result = await response.json();
      
      // Update state with response
      setConversationHistory(prev => [...prev, { 
        role: "assistant", 
        content: result.response 
      }]);
      
      // Speak the response aloud
      speakText(result.response);
      
      setPhase(result.phase || "gathering");
      
      if (result.campaign_plan) {
        setCampaignPlan(result.campaign_plan);
      }
      
      // Execute ONLY when AI response contains 🚀 emoji
      // This emoji is added by the backend ONLY when:
      // 1. User has seen the plan in a previous message
      // 2. User explicitly confirms (says "yes", "confirm", etc.)
      // Execute when:
      // 1. AI response contains 🚀 (indicates explicit confirmation received), OR
      // 2. Phase is "ready" and can_proceed is true (backend confirmed ready state)
      // This ensures content is ONLY created after full conversation and client approval
      const shouldExecute = (result.response && result.response.includes("🚀")) || 
                           (result.phase === "ready" && result.can_proceed === true);
      
      console.log("Response check:", {
        hasResponse: !!result.response,
        hasRocket: result.response?.includes("🚀"),
        phase: result.phase,
        canProceed: result.can_proceed,
        isExecuting,
        shouldExecute
      });
      
      if (shouldExecute && !isExecuting) {
        console.log("Triggering execution...");
        await executeApprovedPlan();
      }
      
    } catch (error) {
      console.error("Message error:", error);
      const errorMsg = "I'm having trouble processing that. Could you try again?";
      setConversationHistory(prev => [...prev, {
        role: "assistant",
        content: errorMsg
      }]);
      speakText(errorMsg);
      toast.error("Failed to process message");
    } finally {
      setLoading(false);
    }
  };

  const executeApprovedPlan = async () => {
    console.log("executeApprovedPlan called, sessionId:", sessionId);
    setIsExecuting(true);
    setPhase("executing");
    
    // Check if we're generating videos (they take much longer)
    const hasVideos = campaignPlan?.content_type?.toLowerCase().includes('video') || campaignPlan?.video_count > 0;
    
    // Add executing message
    const executingMsg = hasVideos 
      ? "Creating your campaign now! Video generation with Sora AI takes 8-10 minutes. Please be patient while we create high-quality content for you."
      : "Creating your campaign now! Generating AI images and content for each platform. This may take 1-2 minutes...";
    
    setConversationHistory(prev => [...prev, {
      role: "assistant",
      content: hasVideos 
        ? "🚀 " + executingMsg + "\n\n⏳ Video generation takes 8-10 minutes. Please wait..."
        : "🚀 " + executingMsg + "\n\n⏳ This may take 1-2 minutes..."
    }]);
    speakText(executingMsg);
    
    try {
      const token = localStorage.getItem('token');
      const userId = getUserId();
      
      const response = await fetch(`${API}/planning/execute`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          session_id: sessionId,
          user_id: userId || "system"
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setPhase("done");
        
        // Check if there were any failures (like video generation failing)
        if (result.has_failures) {
          const failureMsg = result.failure_message || "Some content failed to generate.";
          const partialMsg = `Campaign created with partial success. ${result.content_created || 0} content pieces created, but ${result.videos_failed || 0} video(s) failed to generate due to technical issues. ${failureMsg} You can retry failed content from the Content Library.`;
          setConversationHistory(prev => [...prev, {
            role: "assistant",
            content: `⚠️ Campaign created with partial success!\n\n📊 Stats:\n• ${result.content_created || 0} content pieces created\n• ${result.images_generated || 0} AI images generated\n• ${result.videos_generated || 0} AI videos generated\n• ❌ ${result.videos_failed || 0} video(s) failed\n\n⚠️ ${failureMsg}\n\nYou can retry failed content from the Content Library.`
          }]);
          speakText(partialMsg);
          toast.warning(failureMsg);
        }
        // Check if content is being processed in background
        else if (result.status === "processing") {
          const processingMsg = `Campaign started! ${result.message} Your content is being generated in the background. You can close this dialog and check the Content Library in a few minutes to see your new content.`;
          setConversationHistory(prev => [...prev, {
            role: "assistant",
            content: `🚀 Campaign started!\n\n⏳ ${result.message}\n\nYour content is being generated in the background. You can close this dialog and check the Content Library in a few minutes to see your new content.`
          }]);
          speakText(processingMsg);
          toast.success("Content generation started! Check Content Library shortly.");
        } else {
          const successMsg = `Campaign created successfully! ${result.content_created || 0} content pieces created, ${result.images_generated || 0} AI images generated, ${result.videos_generated || 0} AI videos generated, ${result.posts_scheduled || 0} posts scheduled. You can view and manage your content in the Content Library or Calendar.`;
          setConversationHistory(prev => [...prev, {
            role: "assistant",
            content: `✅ Campaign created successfully!\n\n📊 Stats:\n• ${result.content_created || 0} content pieces created\n• ${result.images_generated || 0} AI images generated\n• ${result.videos_generated || 0} AI videos generated\n• ${result.posts_scheduled || 0} posts scheduled\n\nYou can view and manage your content in the Content Library or Calendar.`
          }]);
          speakText(successMsg);
          toast.success(`Campaign created with ${result.content_created} content pieces!`);
        }
        
        // Call onSuccess callback to refresh dashboard data
        onSuccess?.();
      } else {
        throw new Error(result.error || "Execution failed");
      }
      
    } catch (error) {
      console.error("Execution error:", error);
      const errorMsg = `There was an issue: ${error.message}. Would you like to try again?`;
      setConversationHistory(prev => [...prev, {
        role: "assistant",
        content: `⚠️ There was an issue: ${error.message}\n\nWould you like to try again?`
      }]);
      speakText(errorMsg);
      toast.error("Failed to create campaign");
      setPhase("confirmation");
    } finally {
      setIsExecuting(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        
        try {
          setLoading(true);
          
          const formData = new FormData();
          const fileExtension = mimeType.includes('mp4') ? 'mp4' : 'webm';
          formData.append('audio', audioBlob, `recording.${fileExtension}`);
          formData.append('language', selectedLanguage); // Use selected language
          formData.append('session_id', sessionId);
          formData.append('conversation_history', JSON.stringify(conversationHistory.slice(1)));
          
          const voiceResponse = await fetch(`${API}/voice/command/conversational`, {
            method: 'POST',
            body: formData
          });
          
          if (!voiceResponse.ok) throw new Error('Voice processing failed');
          
          const result = await voiceResponse.json();
          
          if (result.transcribed_text) {
            // Add user message
            setConversationHistory(prev => [...prev, { 
              role: "user", 
              content: result.transcribed_text 
            }]);
            
            // Add AI response
            if (result.response) {
              setConversationHistory(prev => [...prev, { 
                role: "assistant", 
                content: result.response 
              }]);
              // Speak the response aloud
              speakText(result.response);
            }
            
            setPhase(result.phase || "gathering");
            
            if (result.campaign_plan) {
              setCampaignPlan(result.campaign_plan);
            }
            
            // Execute when:
            // 1. AI response contains 🚀, OR
            // 2. Phase is "ready" and can_proceed is true
            const shouldExecuteVoice = (result.response && result.response.includes("🚀")) ||
                                      (result.phase === "ready" && result.can_proceed === true);
            if (shouldExecuteVoice) {
              await executeApprovedPlan();
            }
          }
          
        } catch (error) {
          toast.error("Failed to process voice");
        } finally {
          setLoading(false);
          stream.getTracks().forEach(track => track.stop());
        }
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
    } catch (error) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleClose = () => {
    setMessage("");
    setConversationHistory([]);
    setCampaignPlan(null);
    setSessionId(null);
    onClose();
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'short_video':
      case 'video':
      case 'reels':
        return <Video className="w-4 h-4" />;
      case 'image':
      case 'carousel':
        return <Image className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="glass-card rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary/20">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">YASNO Campaign Planner</h3>
                <p className="text-xs text-muted-foreground">
                  {showLanguageSelect && "Step 1: Choose your language"}
                  {showWebsiteInput && "Step 2: Share your website"}
                  {showConversation && phase === "gathering" && "Tell me about your campaign"}
                  {phase === "planning" && "Creating your plan..."}
                  {phase === "confirmation" && "Review and approve your plan"}
                  {phase === "executing" && "Creating content with AI..."}
                  {phase === "done" && "Campaign created!"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Voice Toggle Button */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleVoice}
                className={`relative ${voiceEnabled ? 'text-primary' : 'text-muted-foreground'}`}
                title={voiceEnabled ? "Voice responses ON - Click to mute" : "Voice responses OFF - Click to unmute"}
                data-testid="voice-toggle-btn"
              >
                {isSpeaking && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full animate-pulse" />
                )}
                {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleClose} data-testid="voice-modal-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Language Selection Screen - Shows FIRST */}
          {showLanguageSelect && (
            <div className="flex-1 p-6 flex flex-col items-center justify-center min-h-[400px]">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-lg"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Languages className="w-10 h-10 text-primary" />
                </div>
                
                <h2 className="text-2xl font-bold mb-2">Choose Your Language</h2>
                <p className="text-muted-foreground mb-6">
                  YASNO speaks 12 languages. Choose the language you&apos;d like to interact in.
                </p>
                
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code)}
                      className={`p-3 rounded-xl border transition-all hover:scale-105 ${
                        selectedLanguage === lang.code 
                          ? 'bg-primary/20 border-primary' 
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span className="text-2xl block mb-1">{lang.flag}</span>
                      <span className="text-xs">{lang.name}</span>
                    </button>
                  ))}
                </div>
                
                <Button 
                  onClick={() => handleLanguageSelect(selectedLanguage)}
                  className="w-full h-12 text-base bg-primary hover:bg-primary/90"
                  data-testid="continue-language-btn"
                >
                  Continue with {LANGUAGES.find(l => l.code === selectedLanguage)?.name}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </div>
          )}

          {/* Website URL Input Screen - Shows SECOND */}
          {showWebsiteInput && (
            <div className="flex-1 p-6 flex flex-col items-center justify-center min-h-[400px]">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-md"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Globe className="w-10 h-10 text-primary" />
                </div>
                
                <h2 className="text-2xl font-bold mb-2">Share Your Website</h2>
                <p className="text-muted-foreground mb-6">
                  I&apos;ll analyze your website to understand your business better and create more personalized content.
                </p>
                
                <div className="space-y-4">
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && websiteUrl.trim() && startConversation()}
                      placeholder="Enter your website URL (e.g., example.com)"
                      className="pl-10 bg-white/5 border-white/10 h-12 text-base"
                      data-testid="website-url-input"
                    />
                  </div>
                  
                  <Button 
                    onClick={startConversation}
                    disabled={loading}
                    className="w-full h-12 text-base bg-primary hover:bg-primary/90"
                    data-testid="analyze-website-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analyzing Website...
                      </>
                    ) : (
                      <>
                        {websiteUrl.trim() ? "Analyze Website & Start" : "Start Without Website"}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                  
                  {websiteUrl.trim() && (
                    <button
                      onClick={skipWebsite}
                      className="text-sm text-muted-foreground hover:text-white transition-colors"
                    >
                      Skip website analysis →
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setShowWebsiteInput(false);
                      setShowLanguageSelect(true);
                    }}
                    className="text-sm text-muted-foreground hover:text-white transition-colors block mt-2"
                  >
                    ← Change language ({LANGUAGES.find(l => l.code === selectedLanguage)?.flag} {LANGUAGES.find(l => l.code === selectedLanguage)?.name})
                  </button>
                </div>
                
                <div className="mt-8 pt-6 border-t border-white/10">
                  <p className="text-xs text-muted-foreground mb-3">What I&apos;ll discover from your website:</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {["Business Name", "Industry", "Products/Services", "Target Audience", "Brand Style"].map((item) => (
                      <span key={item} className="px-3 py-1 rounded-full bg-white/5 text-xs text-muted-foreground">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Conversation Area - Shows after website step */}
          {showConversation && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[400px]">
                {conversationHistory.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === "user" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-white/5 border border-white/10"
                }`}>
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-2 mb-1 text-xs text-primary">
                      <MessageCircle className="w-3 h-3" />
                      <span>YASNO</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-line">{msg.content}</p>
                </div>
              </motion.div>
            ))}
            
            {/* Loading indicator */}
            {loading && !isExecuting && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              </motion.div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Campaign Plan Preview */}
          {campaignPlan && phase !== "done" && (
            <div className="mx-4 mb-4 p-4 rounded-xl bg-primary/10 border border-primary/30">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-primary" />
                <span className="font-medium">Campaign Plan</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Company:</span>
                  <p className="font-medium">{campaignPlan.company_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Industry:</span>
                  <p className="font-medium">{campaignPlan.industry}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <p className="font-medium">{campaignPlan.duration_days} days</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Content:</span>
                  <p className="font-medium">{campaignPlan.total_content_pieces} pieces</p>
                </div>
              </div>
              
              {/* Platform breakdown */}
              {campaignPlan.platforms && (
                <div className="mt-3 pt-3 border-t border-primary/20">
                  <span className="text-xs text-muted-foreground">Content per platform:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(campaignPlan.platforms).map(([platform, config]) => (
                      <div 
                        key={platform}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/5 text-xs"
                      >
                        {getContentTypeIcon(config.content_type)}
                        <span className="capitalize">{platform}</span>
                        <span className="text-muted-foreground">({config.pieces_count})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Success Actions */}
          {phase === "done" && (
            <div className="mx-4 mb-4 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Check className="w-5 h-5 text-green-400" />
                <span className="font-medium text-green-400">Campaign Created!</span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Your content has been generated with AI images and scheduled. View it in:
              </p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={() => {
                    handleClose();
                    window.location.href = '/calendar';
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="view-calendar-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  View Calendar
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    handleClose();
                    window.location.href = '/content-library';
                  }}
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                  data-testid="view-content-btn"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Content Library
                </Button>
              </div>
            </div>
          )}

          {/* Voice Recording Button */}
          <div className="flex justify-center py-3 border-t border-white/10">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading || isExecuting}
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                isRecording 
                  ? 'bg-green-500 animate-pulse scale-110' 
                  : loading || isExecuting
                    ? 'bg-gray-500/50 cursor-not-allowed'
                    : 'bg-primary/20 hover:bg-primary/30 hover:scale-105'
              }`}
              data-testid="voice-record-btn"
            >
              {isRecording ? (
                <MicOff className="w-8 h-8 text-white" />
              ) : (
                <Mic className="w-8 h-8 text-primary" />
              )}
            </button>
          </div>
          <p className="text-center text-xs text-muted-foreground pb-2">
            {isRecording ? '🔴 Recording... Click to stop' : 'Tap to speak or type below'}
          </p>

          {/* Text Input */}
          <div className="flex gap-2 p-4 pt-0">
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !loading && !isExecuting && sendMessage()}
              placeholder={phase === "done" ? "Campaign created! Close to continue." : "Type your message..."}
              className="flex-1 bg-white/5 border-white/10"
              disabled={loading || isExecuting || phase === "done"}
              data-testid="voice-modal-input"
            />
            <Button 
              onClick={sendMessage} 
              disabled={loading || isExecuting || !message.trim() || phase === "done"}
              className="bg-primary hover:bg-primary/90"
              data-testid="voice-modal-send"
            >
              {loading || isExecuting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>

          {/* Quick Examples */}
          {showConversation && conversationHistory.length <= 2 && phase === "gathering" && (
            <div className="px-4 pb-4">
              <p className="text-xs text-muted-foreground mb-2">Try saying:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  "I want to grow on TikTok with images",
                  "Create video content for Instagram",
                  "I need social media content for my brand"
                ].map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setMessage(example)}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
